import { PropsWithChildren, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Container, Link, Stack, Typography as T, Tooltip } from "@mui/material";
import CohortList from "cohorts/components/CohortList";
import CreateCohort from "cohorts/components/CreateCohort";
import useCohorts from "cohorts/hooks/useCohorts";
import useProjects from "rwb/hooks/useProjects";
import CreateProject from "rwb/pages/projects/components/CreateProject";
import ProjectList from "rwb/pages/projects/components/ProjectList";
import yn from "yn";
import {
  CampaignToolIcon,
  ParticipantSearchToolIcon,
  PlusIcon,
  SurveyToolIcon,
  TaskToolIcon,
} from "~/assets/icons";
import CardGrid from "~/components/CardGrid";
import DataBrowserGrid from "~/components/DataBrowserGrid";
import InteractiveCard, { IconContainer } from "~/components/InteractiveCard";
import useAppHeader from "~/contexts/header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import {
  ROUTE_DEFINITIONS,
  ROUTE_OUTREACH,
  ROUTE_PARTICIPANTS,
  ROUTE_SM,
  ROUTE_SURVEYS,
  ROUTE_TASKS,
} from "~/router/constants";
import { VIEW_SM_APP } from "~/router/permissions";
import { useVisibleRoutes } from "~/router/useVisibleRoutes";

const { VITE_COHORT_EXPLORER_ENABLED, VITE_RWB_ENABLED, VITE_SM_ENABLED } = import.meta.env;

const Section = (props: { title: string; actions?: ReactNode } & PropsWithChildren) => (
  <Box component="section">
    <Container sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <T component="h2" variant="h6" fontSize={[18, 20]} fontWeight={600} lineHeight={1.2}>
          {props.title}
        </T>
        <Box display="flex" alignItems="stretch" gap={1}>
          {props.actions}
        </Box>
      </Box>
      {props.children}
    </Container>
  </Box>
);

const StudyManagementWidget = () => {
  const user = useUser();
  const permissions = user?.permissions ?? [];
  const links = [
    {
      url: `${ROUTE_SM}/${ROUTE_PARTICIPANTS}`,
      icon: <ParticipantSearchToolIcon />,
      title: "Search Participants",
      description:
        "Look up participants to view detailed information about their demographics and study participation.",
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_PARTICIPANTS}`],
    },
    {
      url: `${ROUTE_SM}/${ROUTE_SURVEYS}`,
      icon: <SurveyToolIcon />,
      title: "Manage Surveys",
      description: "Build a survey to send to participants.",
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_SURVEYS}`],
    },
    {
      url: `${ROUTE_SM}/${ROUTE_OUTREACH}`,
      icon: <CampaignToolIcon />,
      title: "Contact Participants",
      description: "Send surveys or other study-related information to a group of participants.",
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_OUTREACH}`],
    },
    {
      url: `${ROUTE_SM}/${ROUTE_TASKS}`,
      icon: <TaskToolIcon />,
      title: "Manage Tasks",
      description:
        "Manage tasks related to participants' self-reported cancers, revocations or specific study information.",
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_TASKS}`],
    },
  ];
  const visibleLinks = useVisibleRoutes(permissions, links);

  if (visibleLinks.length === 0) {
    return null; // Don't render the widget if there are no visible links
  }

  return (
    <Section title="Study Management">
      <CardGrid>
        {visibleLinks.map((link) => (
          <Link key={link.url} component={RouterLink} to={link.url} underline="none">
            <InteractiveCard
              sx={{
                px: 3,
                py: 2.5,
                gap: 1,
                height: 200,
                justifyContent: "space-between",
              }}
            >
              <IconContainer color="primary.main">{link.icon}</IconContainer>
              <T variant="body1" fontWeight={600}>
                {link.title}
              </T>
              <T
                variant="body2"
                sx={{
                  color: "grey.700",
                  display: "-webkit-box",
                  flex: 1,
                  fontSize: 12,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "4",
                }}
              >
                {link.description}
              </T>
            </InteractiveCard>
          </Link>
        ))}
      </CardGrid>
    </Section>
  );
};

const WorkbenchWidget = () => {
  const limit = 8;
  const projects = useProjects(limit);
  const loading = projects.isLoading;
  const empty = !loading && !projects.data?.total;

  return (
    <Section
      title="Workbenches"
      actions={
        !empty && (
          <Box display="flex" alignItems="center" gap={1}>
            <CreateProject
              callToAction={
                <Tooltip arrow enterDelay={500} title="Create a workbench" placement="top">
                  <Button
                    aria-label="Create workbench"
                    variant="outlined"
                    size="small"
                    sx={{ aspectRatio: 1, borderRadius: 2, height: 38 }}
                  >
                    <PlusIcon />
                  </Button>
                </Tooltip>
              }
            />
            {Number(projects.data?.total) > limit && (
              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
                component={RouterLink}
                to="/workbenches"
              >
                View All
              </Button>
            )}
          </Box>
        )
      }
    >
      <ProjectList data={projects} pageSize={limit} showPagination={false} />
    </Section>
  );
};

const DataBrowserWidget = () => {
  return (
    <Section title="Data Browser">
      <DataBrowserGrid />
    </Section>
  );
};

const CohortExplorerWidget = () => {
  const limit = 4;
  // TODO: refactor below when backend support for pagination is completed
  const { data: cohorts, isLoading } = useCohorts();
  const empty = !isLoading && cohorts?.length === 0;

  return (
    <Section
      title="Cohort Explorer"
      actions={
        !empty && (
          <Box display="flex" alignItems="center" gap={1}>
            <CreateCohort
              callToAction={
                <Tooltip arrow enterDelay={500} title="Create a cohort" placement="top">
                  <Button variant="outlined" size="small" sx={{ aspectRatio: 1, height: 38 }}>
                    <PlusIcon />
                  </Button>
                </Tooltip>
              }
            />
            {Number(cohorts?.length) > limit && (
              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
                component={RouterLink}
                to="/cohorts"
              >
                View All
              </Button>
            )}
          </Box>
        )
      }
    >
      <CohortList limit={limit} />
    </Section>
  );
};

const title = "Home";

const Home = () => {
  const user = useUser();
  useDocumentTitle(title);
  useAppHeader({ title });
  const showDb = !!user;
  const showRwb = yn(VITE_RWB_ENABLED);
  const showSm = yn(VITE_SM_ENABLED) && user?.permissions?.includes(VIEW_SM_APP);
  const showCe = yn(VITE_COHORT_EXPLORER_ENABLED);

  return (
    <Stack bgcolor="common.dimmedPageBackground" minHeight="100%" gap={6} pt={3} pb={20}>
      {showSm && <StudyManagementWidget />}
      {showRwb && <WorkbenchWidget />}
      {showDb && <DataBrowserWidget />}
      {showCe && <CohortExplorerWidget />}
    </Stack>
  );
};

export default Home;
